import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { streetTypesApi } from '../api/streetsApi'

/**
 * Street Type Modal Component
 * Form for creating/editing street types
 */
export function StreetTypeModal({ isOpen, onClose, streetType }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!streetType

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type_street: '',
      short_name: '',
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (streetType) {
      reset({
        type_street: streetType.type_street || '',
        short_name: streetType.short_name || '',
      })
    } else {
      reset({ type_street: '', short_name: '' })
    }
  }, [streetType, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      return isEdit ? streetTypesApi.update(streetType.id, data) : streetTypesApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['street-types'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit ? t('streets.streetTypeUpdated') : t('streets.streetTypeCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error?.message || t('streets.saveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('streets.editStreetType') : t('streets.createStreetType')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="type_street"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.typeStreet')} *`}
                      placeholder={t('streets.typeStreetPlaceholder')}
                      error={errors.type_street?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="short_name"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.shortName')} *`}
                      placeholder={t('streets.shortNamePlaceholder')}
                      error={errors.short_name?.message}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              loading={saveMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}
